/**
 * Transport Rules Types - Unified Exchange API
 *
 * Type definitions for Exchange Online Rules via unified API with type dispatch pattern.
 * Replaces dedicated transport rules endpoints with single unified Exchange API.
 */

// ============================================================================
// UNIFIED EXCHANGE API TYPES
// ============================================================================

/**
 * Unified Exchange Rule Request for creating rules via type dispatch.
 * Supports transport rules now, extensible to DLP, retention, etc.
 */
export interface ExchangeRuleRequest {
    /** Rule category (transport, dlp, retention, etc.) */
    type: 'transport';
    /** Specific rule type within category */
    rule_type: 'block-external-senders' | 'block-auto-forward' | 'block-client-domain';
    /** Optional custom rule name (defaults to standard name) */
    name?: string;
    /** Rule priority (0 = highest, default: 0) */
    priority?: number;
    /** Rule enabled state (default: true) */
    enabled?: boolean;
    /**
     * Rule-specific configuration:
     * - block-client-domain: { client_domain: string }
     * - block-external-senders: { allowed_domains?: string[] }
     * - block-auto-forward: {} (no configuration)
     */
    configuration?: Record<string, unknown>;
}

/**
 * Unified Exchange Rule Update Request for PATCH operations.
 * All fields optional, only provided fields will be updated.
 */
export interface ExchangeRuleUpdateRequest {
    /** Update rule name */
    name?: string;
    /** Update rule priority */
    priority?: number;
    /** Update rule enabled state */
    enabled?: boolean;
    /** Update rule-specific configuration (e.g., allowed_domains) */
    configuration?: Record<string, unknown>;
}

/**
 * Unified Exchange Rule Response from Exchange Online.
 * Uses PascalCase to match Exchange Online API conventions.
 */
export interface ExchangeRuleResponse {
    /** Rule identity/ID from Exchange Online */
    Identity: string;
    /** Rule display name */
    Name: string;
    /** Rule priority (0 = highest) */
    Priority: number;
    /** Rule state (Enabled/Disabled) */
    State: 'Enabled' | 'Disabled';
    /** Optional rule type identifier */
    RuleType?: string;
    /** Optional rule comments */
    Comments?: string;
    /** Optional from address patterns (for block-client-domain rules) */
    FromAddressMatchesPatterns?: string[];
    /** Optional allowed domains exception list (for block-external-senders rules) */
    ExceptIfRecipientDomainIs?: string | string[];
    /** Optional Exchange-specific properties */
    [key: string]: unknown;
}

/**
 * List response from unified Exchange API.
 * Includes rules array and total count.
 */
export interface ExchangeRuleListResponse {
    /** Array of Exchange rules */
    rules: ExchangeRuleResponse[];
    /** Total count of rules */
    total: number;
}

// ============================================================================
// BACKWARD COMPATIBILITY TYPES (DEPRECATED)
// ============================================================================

/**
 * @deprecated Use ExchangeRuleResponse instead
 * Kept for backward compatibility during migration
 */
export type TransportRule = ExchangeRuleResponse;

/**
 * @deprecated Use ExchangeRuleRequest instead
 * Request to create a rule with priority
 */
export interface CreateRulePriorityRequest {
    priority: number;
    enabled?: boolean;
}

/**
 * @deprecated Use ExchangeRuleRequest with configuration.client_domain instead
 * Request to create a block client domain rule
 */
export interface CreateClientDomainRuleRequest {
    client_domain: string;
    priority: number;
    enabled?: boolean;
}

/**
 * @deprecated Use ExchangeRuleUpdateRequest instead
 * Request to update rule priority
 */
export interface UpdatePriorityRequest {
    priority: number;
}

/**
 * Rule types supported by the application
 */
export type TransportRuleType =
    | 'block-external-senders'
    | 'block-auto-forward'
    | 'block-client-domain';

/**
 * Display names for rule types
 */
export const TRANSPORT_RULE_TYPE_DISPLAY_NAMES: Record<TransportRuleType, string> = {
    'block-external-senders': 'Block External Senders',
    'block-auto-forward': 'Block Auto-Forward to External Recipients',
    'block-client-domain': 'Block Client Domain Spoofing'
};

/**
 * Helper function to determine rule type from rule name
 */
export function getRuleType(rule: TransportRule): TransportRuleType | 'unknown' {
    const name = rule.Name.toLowerCase();

    if (name.includes('block external senders')) {
        return 'block-external-senders';
    }
    if (name.includes('block auto-forward')) {
        return 'block-auto-forward';
    }
    if (name.includes('block client domain')) {
        return 'block-client-domain';
    }

    return 'unknown';
}
